import { useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, MailCheck, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export const Field = ({ id, label, hint, type = "text", ...p }) => {
  const [show, setShow] = useState(false);
  const pw = type === "password";
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <span className="relative block">
        <input data-testid={id} type={pw && show ? "text" : type} {...p}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-slate-900 outline-none transition-[border-color,box-shadow] placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30" />
        {pw && <button type="button" data-testid={`${id}-toggle`} onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-700" aria-label={show ? "Hide password" : "Show password"}>{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>}
      </span>
      {hint && <span className="block text-xs text-slate-400">{hint}</span>}
    </label>
  );
};

const GoogleButton = ({ next }) => (
  <button type="button" data-testid="google-signin-btn"
    onClick={() => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + next;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }}
    className="flex w-full items-center justify-center gap-3 rounded-full border border-slate-300 bg-white py-2.5 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50">
    <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
    Continue with Google
  </button>
);

const Submit = ({ busy, children, id }) => (
  <button data-testid={id} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-indigo-600 py-3 text-sm font-bold text-white transition-colors hover:bg-indigo-700 disabled:opacity-60">
    {busy && <Loader2 className="h-4 w-4 animate-spin" />}{children}
  </button>
);

const SignIn = ({ onDone, go }) => {
  const { login } = useAuth();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false), [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { onDone(await login(f)); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="signin-form">
      <Field id="signin-email-input" label="Email" type="email" autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
      <Field id="signin-password-input" label="Password" type="password" autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required />
      <div className="flex justify-end"><button type="button" data-testid="forgot-password-link" onClick={() => go("forgot", f.email)} className="text-xs font-semibold text-indigo-600 hover:underline">Forgot password?</button></div>
      {err && <p data-testid="auth-error" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</p>}
      <Submit busy={busy} id="signin-submit-btn">Sign in</Submit>
    </form>
  );
};

const Register = ({ onDone }) => {
  const { register } = useAuth();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone_number: "", password: "", confirm_password: "" });
  const [busy, setBusy] = useState(false), [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const mismatch = f.confirm_password && f.password !== f.confirm_password;
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (f.password !== f.confirm_password) return setErr("Passwords do not match");
    setBusy(true);
    try { onDone(await register(f), true); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="register-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="register-first-name-input" label="First name" autoComplete="given-name" value={f.first_name} onChange={set("first_name")} required />
        <Field id="register-last-name-input" label="Last name" autoComplete="family-name" value={f.last_name} onChange={set("last_name")} required />
      </div>
      <Field id="register-email-input" label="Email" type="email" autoComplete="email" value={f.email} onChange={set("email")} required />
      <Field id="register-phone-input" label="Phone number" type="tel" autoComplete="tel" placeholder="+1 416 555 0100" value={f.phone_number} onChange={set("phone_number")} required />
      <Field id="register-password-input" label="Password" type="password" autoComplete="new-password" hint="At least 8 characters with a letter and a number" value={f.password} onChange={set("password")} required minLength={8} />
      <Field id="register-confirm-password-input" label="Confirm password" type="password" autoComplete="new-password" value={f.confirm_password} onChange={set("confirm_password")} required />
      {mismatch && <p data-testid="register-mismatch" className="text-xs font-semibold text-rose-600">Passwords do not match</p>}
      {err && <p data-testid="auth-error" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</p>}
      <Submit busy={busy} id="register-submit-btn">Create account</Submit>
    </form>
  );
};

const Forgot = ({ email0, go }) => {
  const [email, setEmail] = useState(email0 || "");
  const [busy, setBusy] = useState(false), [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { const { data } = await api.post("/auth/password/forgot", { email }); toast.success(data.message); go("reset", email); }
    catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
      <p className="text-sm text-slate-600">Enter your account email and we'll send a 6-digit code from support@lexversant.ca.</p>
      <Field id="forgot-email-input" label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      {err && <p data-testid="auth-error" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</p>}
      <Submit busy={busy} id="forgot-submit-btn">Send reset code</Submit>
      <button type="button" data-testid="have-code-link" onClick={() => go("reset", email)} className="w-full text-center text-xs font-semibold text-indigo-600 hover:underline">I already have a code</button>
    </form>
  );
};

const Reset = ({ email0, onDone }) => {
  const { applySession } = useAuth();
  const [f, setF] = useState({ email: email0 || "", code: "", password: "", confirm_password: "" });
  const [busy, setBusy] = useState(false), [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: k === "code" ? e.target.value.replace(/\D/g, "").slice(0, 6) : e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (f.password !== f.confirm_password) return setErr("Passwords do not match");
    setBusy(true);
    try { const { data } = await api.post("/auth/password/reset", f); toast.success("Password updated. You're signed in."); onDone(await applySession(data)); }
    catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
      <Field id="reset-email-input" label="Email" type="email" value={f.email} onChange={set("email")} required />
      <Field id="reset-code-input" label="6-digit code" inputMode="numeric" autoComplete="one-time-code" value={f.code} onChange={set("code")} required minLength={6} />
      <Field id="reset-password-input" label="New password" type="password" autoComplete="new-password" hint="At least 8 characters with a letter and a number" value={f.password} onChange={set("password")} required minLength={8} />
      <Field id="reset-confirm-password-input" label="Confirm new password" type="password" autoComplete="new-password" value={f.confirm_password} onChange={set("confirm_password")} required />
      {err && <p data-testid="auth-error" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</p>}
      <Submit busy={busy} id="reset-submit-btn">Update password</Submit>
    </form>
  );
};

export const VerifyEmail = ({ onDone, compact, sendError }) => {
  const { setUser, refresh } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false), [err, setErr] = useState("");
  const resend = async () => { try { await api.post("/auth/verify-email/send"); toast.success("A new code is on its way."); } catch (x) { toast.error(errMsg(x)); } };
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { const { data } = await api.post("/auth/verify-email/confirm", { code }); setUser(data); await refresh(); toast.success("Email verified"); onDone?.(); }
    catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="verify-form">
      {!compact && (sendError
        ? <div data-testid="verify-send-error" className="flex items-start gap-3 rounded-xl bg-amber-50 p-4 text-sm text-amber-900"><MailCheck className="mt-0.5 h-5 w-5 shrink-0" />{sendError} You can try "Resend code" later.</div>
        : <div className="flex items-start gap-3 rounded-xl bg-indigo-50 p-4 text-sm text-indigo-900"><MailCheck className="mt-0.5 h-5 w-5 shrink-0" />We sent a 6-digit code to your email. Enter it to verify your account.</div>)}
      <Field id="verify-code-input" label="Verification code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} required minLength={6} />
      {err && <p data-testid="auth-error" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</p>}
      <Submit busy={busy} id="verify-submit-btn">Verify email</Submit>
      <div className="flex justify-between text-xs font-semibold">
        <button type="button" data-testid="verify-resend-btn" onClick={resend} className="text-indigo-600 hover:underline">Resend code</button>
        {onDone && <button type="button" data-testid="verify-later-btn" onClick={onDone} className="text-slate-500 hover:underline">Do this later</button>}
      </div>
    </form>
  );
};

const TITLES = { signin: "Welcome back", register: "Create your account", forgot: "Reset your password", reset: "Enter your reset code", verify: "Verify your email" };

export default function Login() {
  const { user, ready } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const next = "/";
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "signin");
  const [email, setEmail] = useState("");
  const go = (m, e) => { setMode(m); if (e !== undefined) setEmail(e); };
  // Signing in on this page: stay here until onDone decides (e.g. show the verify step)
  const authing = useRef(false);
  const finish = () => nav(next, { replace: true });
  const [sendErr, setSendErr] = useState("");
  const onDone = (u, isNew) => {
    toast.success(`Welcome, ${u.first_name || u.name}.`);
    if (isNew && u.verification_sent === false) setSendErr("We couldn't send your verification code just now.");
    if (isNew && !u.email_verified) setMode("verify"); else finish();
  };
  if (ready && user && !authing.current && mode !== "verify") return <Navigate to={next} replace />;
  const tab = (m, label) => <button type="button" data-testid={`auth-tab-${m}`} onClick={() => setMode(m)} className={`flex-1 rounded-full py-2 text-sm font-bold transition-colors ${mode === m ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>{label}</button>;
  return (
    <div className="mx-auto grid max-w-6xl gap-10 px-4 py-10 sm:px-8 lg:grid-cols-[1fr_440px] lg:py-16">
      <div className="hidden flex-col justify-center lg:flex">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-indigo-300">LexVersant account</p>
        <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">Practise like it's exam day.</h1>
        <p className="mt-4 max-w-md text-slate-400">One account keeps your scores, practice progress and Practice Pass in sync on any device. For your security, signing in on a new device signs out the old one.</p>
        <ul className="mt-8 space-y-3 text-sm text-slate-300">
          {["13 full-length mock tests, Easy to Hard", "3,780 practice questions across Parts A–I", "AI speaking tips, tutor chat and study plans"].map((t) => <li key={t} className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400" />{t}</li>)}
        </ul>
      </div>
      <div data-testid="auth-card" className="animate-rise rounded-2xl bg-white p-6 shadow-2xl shadow-slate-950/50 sm:p-8">
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">{TITLES[mode]}</h2>
        {(mode === "signin" || mode === "register") && (
          <>
            <div className="mt-5 flex rounded-full bg-slate-100 p-1">{tab("signin", "Sign in")}{tab("register", "Create account")}</div>
            <div className="mt-5"><GoogleButton next={next} /></div>
            <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-widest text-slate-400"><span className="h-px flex-1 bg-slate-200" />or with email<span className="h-px flex-1 bg-slate-200" /></div>
          </>
        )}
        <div className={mode === "signin" || mode === "register" ? "" : "mt-5"} onSubmitCapture={() => { authing.current = true; }}>
          {mode === "signin" && <SignIn onDone={onDone} go={go} />}
          {mode === "register" && <Register onDone={onDone} />}
          {mode === "forgot" && <Forgot email0={email} go={go} />}
          {mode === "reset" && <Reset email0={email} onDone={() => finish()} />}
          {mode === "verify" && <VerifyEmail onDone={finish} sendError={sendErr} />}
        </div>
        {(mode === "forgot" || mode === "reset") && <button data-testid="back-to-signin-btn" onClick={() => setMode("signin")} className="mt-5 w-full text-center text-sm font-semibold text-slate-500 hover:text-slate-900">Back to sign in</button>}
        <p className="mt-6 text-center text-xs text-slate-400">Just looking? <Link to="/" data-testid="auth-free-demo-link" className="font-semibold text-indigo-600 hover:underline">Try the free demo</Link> — no password needed.</p>
      </div>
    </div>
  );
}
